import React from 'react';
import { Gauge, Layers, HelpCircle, PlayCircle, Plus } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { CategorizedMetrics } from './types/metricTypes';
import { formatVersionDisplay } from './utils/metricsFormatters';

interface MetricsHeaderProps {
  showCurrentOnly: boolean;
  actualVersion: string | null;
  categorizedMetrics: CategorizedMetrics;
  onToggleCurrentOnly: (checked: boolean) => void;
  onRunExtracts: () => void;
  onAddMetric: () => void;
}

const MetricsHeader: React.FC<MetricsHeaderProps> = ({
  showCurrentOnly,
  actualVersion,
  categorizedMetrics,
  onToggleCurrentOnly,
  onRunExtracts,
  onAddMetric
}) => {
  return (
    <div className="mt-6 mb-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Pricing Metrics</h2>
      </div>
      {/* Toggle and action buttons */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-2">
          <Switch
            id="show-current-metrics"
            checked={showCurrentOnly}
            onCheckedChange={onToggleCurrentOnly}
          />
          <Label htmlFor="show-current-metrics" className="text-sm">
            {showCurrentOnly
              ? `Show Current Metrics Only ${actualVersion ? `(${formatVersionDisplay(actualVersion)})` : ''}`
              : 'Show All Metrics'}
          </Label>
        </div>
        <div className="flex space-x-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" size="sm" onClick={onRunExtracts}>
                  <PlayCircle className="h-4 w-4 mr-2" /> Run Extracts
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Extract pricing metrics from the pricing page</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button onClick={onAddMetric} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Metric
          </Button>
        </div>
      </div>
      {/* Stats */}
      <div className="flex items-center space-x-3 text-sm text-gray-500">
        <div className="flex items-center">
          <Gauge className="h-4 w-4 text-blue-500 mr-1" />
          <span>{categorizedMetrics.usageLicenseCreditMetrics.length} Modality</span>
        </div>
        <div className="flex items-center">
          <Layers className="h-4 w-4 text-orange-500 mr-1" />
          <span>{categorizedMetrics.thresholdFlatMetrics.length} Threshold</span>
        </div>
        <div className="flex items-center">
          <HelpCircle className="h-4 w-4 text-gray-500 mr-1" />
          <span>{categorizedMetrics.otherMetrics.length} Other</span>
        </div>
      </div>
    </div>
  );
};

export default MetricsHeader;
